// frontend/src/components/athleteEditor/MeasurablesLedger.jsx
// Spreadsheet ledger (redesign screen 1b) — swapped in for MeasurablesCard
// when verifyMode is on (see AdminAthleteProfilePage.js). Drives the exact
// same save/verify brain as MeasurablesCard via useMeasurableSaves (Task 4);
// this file only supplies a different, camp-day-speed layout: one row per
// measurable, a verification cell (who/when/camp), and a VERIFY/UNVERIFY
// button per row. Catalog resolution + column-backed filter + gear fields
// are ported verbatim from MeasurablesCard — see its header comment.
import React, { useState } from 'react';
import { useCatalog } from '../../context/CatalogContext';
import { computeBmi } from '../../lib/athleteDetail';
import { latestVerifierFor } from '../../lib/athleteEditorModel';
import VerifiedFlame from '../VerifiedFlame';
import useMeasurableSaves from './useMeasurableSaves';
import { GEAR_SELECT_FIELDS, orderGroupsSizeFirst } from './MeasurablesCard';

const hasValue = v => v !== null && v !== undefined && v !== '';

// Server timestamps are UTC with no tz suffix — parse as UTC, render local,
// matching VerificationHistoryCard's "Jul 3" mono format.
function fmtShortDate(iso) {
  if (!iso) return '';
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + 'Z');
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function MeasurablesLedger({ userId, profile, perms, apiUrl, getConfig, logs, onChange, onSaved }) {
  const catalog = useCatalog();
  const safe = profile || {};
  const { values, setValues, errors, commitField, toggleVerify } =
    useMeasurableSaves({ userId, profile, apiUrl, getConfig, logs, onChange, onSaved });

  const editable = !!(perms && perms.canEditValues);
  const canVerify = !!(perms && perms.canVerify);

  // Sport -> catalog slug -> grouped measurables, resolved identically to
  // MeasurablesCard, filtered to catalog measurables backed by a flat
  // AthleteProfile column (save/verify endpoints 400 on keyless measurables).
  const sportName = safe.sport || 'Football';
  const slug = (catalog.sports.find(s => s.name === sportName) || {}).slug || 'football';
  const catalogGroups = orderGroupsSizeFirst(catalog.groupedMeasurablesFor(slug)
    .map(g => ({
      category: g.category,
      measurables: g.measurables
        .filter(m => Object.prototype.hasOwnProperty.call(safe, m.key))
        .map(m => ({ key: m.key, label: m.label, unit: m.unit, isProfileField: false })),
    }))
    .filter(g => g.measurables.length > 0));

  // Gear & Preferences — not catalog measurables, so it's an explicit,
  // cross-sport group appended after the catalog ones, same fields as
  // MeasurablesCard (POST .../profile, not update-stat).
  const gearGroup = {
    category: 'Gear & Preferences',
    measurables: [
      { key: 'shoe_size', label: 'Shoe Size', unit: 'US', isProfileField: true },
      ...GEAR_SELECT_FIELDS.map(f => ({ key: f.name, label: f.label, isProfileField: true, options: f.options })),
    ],
  };

  const groups = [...catalogGroups, gearGroup];

  // Empty categories start collapsed; expanding one adds it to this set.
  // A category with any value is always rendered expanded regardless of
  // this set (see isCategoryEmpty below).
  const [expandedCategories, setExpandedCategories] = useState(() => new Set());
  const toggleCategory = (category) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category); else next.add(category);
      return next;
    });
  };

  const isCategoryEmpty = (group) => group.measurables.every(m => !hasValue(values[m.key]));

  // Height is entered as feet + inches everywhere a human types it, matching
  // iOS. The catalog types it as inches and the API stores total inches, so
  // the two selects are purely a display concern — they read from and write
  // back to the same total-inches value.
  const HEIGHT_KEYS = ['height_inches', 'height_inches_with_shoes'];

  // 0, 0.25, … 11.75. Athlete heights are measured, not typed — 30% of stored
  // values sit off a whole inch — so a whole-inch picker would hide that
  // precision and round it away on edit.
  const INCH_STEPS = Array.from({ length: 48 }, (_, i) => i * 0.25);
  const inchLabel = (v) => {
    const whole = Math.floor(v);
    const frac = { 0.25: '¼', 0.5: '½', 0.75: '¾' }[Number((v - whole).toFixed(2))] || '';
    return `${whole}${frac}"`;
  };

  const renderHeightCell = (m) => {
    // Round to the nearest quarter so off-quarter stored values (66.7, from a
    // converted source) land on a selectable option. Because this rounds and
    // nothing is written until the user picks, such a value survives an
    // untouched visit to this row intact.
    const raw = hasValue(values[m.key]) ? Number(values[m.key]) : null;
    const quarters = raw == null || Number.isNaN(raw) ? null : Math.round(raw * 4);
    const feet = quarters == null ? '' : Math.floor(quarters / 48);
    const inches = quarters == null ? 0 : (quarters - feet * 48) / 4;

    const write = (nextFeet, nextInches) => {
      if (nextFeet === '') {
        setValues(v => ({ ...v, [m.key]: '' }));
        if (editable) commitField(m.key, '', !!m.isProfileField);
        return;
      }
      const total = Number(nextFeet) * 12 + Number(nextInches || 0);
      // String() already renders 72 as "72" and 72.25 as "72.25", so a clean
      // height stores without a trailing ".0" like every other measurable.
      const next = String(total);
      setValues(v => ({ ...v, [m.key]: next }));
      if (editable) commitField(m.key, next, !!m.isProfileField);
    };

    return (
      <span className="ae-ledger__height">
        <select
          className={`ae-ledger__val${feet === '' ? ' ae-ledger__val--empty' : ''}`}
          value={feet}
          disabled={!editable}
          aria-label={`${m.label} feet`}
          onChange={e => write(e.target.value, inches)}
        >
          <option value="">—</option>
          {[4, 5, 6, 7, 8].map(f => <option key={f} value={f}>{`${f}'`}</option>)}
        </select>
        <select
          className="ae-ledger__val"
          value={feet === '' ? 0 : inches}
          disabled={!editable || feet === ''}
          aria-label={`${m.label} inches`}
          onChange={e => write(feet === '' ? 5 : feet, e.target.value)}
        >
          {INCH_STEPS.map(i => (
            <option key={i} value={i}>{inchLabel(i)}</option>
          ))}
        </select>
      </span>
    );
  };

  const renderValueCell = (m) => {
    if (HEIGHT_KEYS.includes(m.key)) return renderHeightCell(m);
    if (m.options) {
      return (
        <select
          className="ae-ledger__val"
          value={values[m.key] || ''}
          disabled={!editable}
          onChange={e => { setValues(v => ({ ...v, [m.key]: e.target.value })); editable && commitField(m.key, e.target.value, true); }}
        >
          <option value="">—</option>
          {m.options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    const empty = !hasValue(values[m.key]);
    return (
      <input
        className={`ae-ledger__val${empty ? ' ae-ledger__val--empty' : ''}`}
        type={m.key === 'shoe_size' ? 'number' : 'text'}
        step={m.key === 'shoe_size' ? '0.5' : undefined}
        value={values[m.key] ?? ''}
        disabled={!editable}
        placeholder={empty ? '—' : ''}
        onChange={e => setValues(v => ({ ...v, [m.key]: e.target.value }))}
        onBlur={e => editable && commitField(m.key, e.target.value, !!m.isProfileField)}
      />
    );
  };

  const renderVerifCell = (key) => {
    if (!hasValue(values[key])) {
      return <span className="ae-ledger__verif ae-ledger__verif--none">No value</span>;
    }
    if (values[`${key}_verified`]) {
      const v = latestVerifierFor(logs, key);
      return (
        <span className="ae-ledger__verif ae-ledger__verif--seal">
          <VerifiedFlame />
          {v ? (
            <span>
              {v.name || 'Verified'}
              <span className="ae-ledger__verif-meta">
                {v.date ? ` · ${fmtShortDate(v.date)}` : ''}{v.camp ? ` · ${v.camp}` : ''}
              </span>
            </span>
          ) : 'Verified'}
        </span>
      );
    }
    return <span className="ae-ledger__verif">Not verified</span>;
  };

  const renderAction = (key) => {
    if (!canVerify) return null;
    const verified = hasValue(values[key]) && !!values[`${key}_verified`];
    if (verified) {
      return <button type="button" className="ae-ledger__unverify" onClick={() => toggleVerify(key)}>Unverify</button>;
    }
    const disabled = !hasValue(values[key]);
    return (
      <button type="button" className="ae-ledger__verify" disabled={disabled} onClick={() => !disabled && toggleVerify(key)}>
        Verify
      </button>
    );
  };

  const renderRow = (m) => (
    <div className="ae-ledger__row" key={m.key}>
      <span className="ae-ledger__name">
        {m.label}{m.unit && <span className="ae-ledger__unit"> ({m.unit})</span>}
      </span>
      <div>
        {renderValueCell(m)}
        {errors[m.key] && <div className="form-field__error">{errors[m.key]}</div>}
      </div>
      {renderVerifCell(m.key)}
      <div className="ae-ledger__action">{renderAction(m.key)}</div>
    </div>
  );

  const bmi = computeBmi(values.height_inches, values.weight_lbs);

  return (
    <div className="ae-ledger">
      <div className="ae-card__head">
        <h2 className="ae-card__title">Measurables Ledger</h2>
        <span className="ae-card__hint">VERIFY/UNVERIFY PER ROW · VALUES SAVE ON BLUR</span>
      </div>
      <div className="ae-ledger__head">
        <span>Measurable</span>
        <span>Value</span>
        <span>Verification</span>
        <span>Action</span>
      </div>
      {groups.map(group => {
        const empty = isCategoryEmpty(group);
        const expanded = !empty || expandedCategories.has(group.category);
        if (!expanded) {
          const emptyLabels = group.measurables.map(m => m.label).join(' · ');
          return (
            <div className="ae-ledger__row ae-ledger__collapse" key={group.category}>
              <span className="ae-ledger__cat ae-ledger__cat--muted">{group.category}</span>
              <span className="ae-ledger__meta">{group.measurables.length} empty</span>
              <span className="ae-ledger__meta ae-ledger__meta--list">{emptyLabels}</span>
              <div className="ae-ledger__action">
                <button type="button" className="ae-ledger__expand" onClick={() => toggleCategory(group.category)}>
                  Expand <span className="ae-ledger__chevron">▾</span>
                </button>
              </div>
            </div>
          );
        }
        return (
          <React.Fragment key={group.category}>
            <div className="ae-ledger__catrow ae-ledger__catrow--head">
              <span className="ae-ledger__cat">{group.category}</span>
              {/* Only categories the user opened (empty ones) can collapse;
                  categories that hold values stay open by design. */}
              {empty && (
                <button type="button" className="ae-ledger__expand ae-ledger__expand--open" onClick={() => toggleCategory(group.category)}>
                  Collapse <span className="ae-ledger__chevron">▾</span>
                </button>
              )}
            </div>
            {group.measurables.map(renderRow)}
            {group.category === 'Size' && (
              <div className="ae-ledger__row">
                <span className="ae-ledger__name" style={{ color: '#6B7C8E' }}>BMI <span className="ae-ledger__unit">(derived)</span></span>
                <div className="ae-ledger__val ae-ledger__val--derived" style={{ display: 'flex', alignItems: 'center' }}>{bmi != null ? bmi : '—'}</div>
                <span className="ae-ledger__verif">Computed from height + weight</span>
                <div className="ae-ledger__action" />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
